import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import { StatusCode } from "api/protocols";
import Page from "components/page/Page";
import Card from "components/card/Card";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import { formatDate } from "helpers/Helpers";
import { Select } from "components/select/index";
import Search from "components/search/Search";
import { faMagnifyingGlass, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import { IAccommodationUnitType } from "../unitTypes/UnitTypes";
import { IAccommodationUnit } from "../units/Units";
import { IAccommodationReservation } from "../reservations/types";
import { GetAccommodationReservations } from "api/rpc/2024-04/facilityAdmin/accommodation/reservations";
import { GetAccommodationUnitTypes } from "api/rpc/2024-04/facilityAdmin/accommodation/unitTypes";
import { GetAccommodationUnits } from "api/rpc/2024-04/facilityAdmin/accommodation/units";
import {
  GetAccommodationRatePlanPrices,
  IAccommodationRatePlanPrice,
} from "api/rpc/2024-04/facilityAdmin/accommodation/ratePlans/ratePlanPrices";
import { AccommodationReservationModal } from "components/accommodation/reservationModal/AccommodationReservationModal";
import { ConfirmReservationModal } from "components/accommodation/confirmReservationModal/ConfirmReservationModal";
import { useAccommodationReservation } from "hooks/accommodation/useAccommodationReservation";
import AccommodationReservationChart from "components/accommodation/reservationChart/AccommodationReservationChart";

export interface IGroupedUnitTypeReservations {
  unitType: IAccommodationUnitType;
  units: IAccommodationUnit[];
  reservations: IAccommodationReservation[];
  pricesByDate?: Record<string, number>;
}

export function AccommodationCalendar() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [filterState, setFilterState] = useState({
    startingDate: new Date(),
    endingDate: new Date(new Date().setDate(new Date().getDate() + 9)),
    unit_type_id: 1,
    search: "",
  });

  const [accommodationUnitTypes, setAccommodationUnitTypes] = useState<IAccommodationUnitType[]>([]);
  const [groupedReservations, setGroupedReservations] = useState<IGroupedUnitTypeReservations[]>([]);
  const [filteredReservations, setFilteredReservations] = useState<IGroupedUnitTypeReservations[]>([]);

  const {
    mergedUnitTypes,
    selectedUnitTypes,
    accommodationCategories,
    customerPaymentMethods,
    currentStep,
    steps,
    rooms,
    reservationCompleteState,
    newReservationState,
    billingState,
    setNewReservationState,
    setBillingState,
    setRooms,
    setCurrentStep,
    handleContinue,
    handleBack,
    handleCheckboxChange,
    handleCustomerSelection,
    handleCardInputChange,
    handleBookReservation,
    handleOpenNewReservation,
    handleCloseConfirmation,
    handleCloseModal,
  } = useAccommodationReservation({
    onSuccess: () => void loadAccommodationReservationData(),
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadAccommodationReservationData(source?.token);
  }, [filterState?.startingDate, filterState?.endingDate]);

  useEffect(() => {
    if (!groupedReservations) {
      return;
    }

    const search = filterState.search.trim().toLowerCase();
    let filteredGroups = groupedReservations;

    if (filterState.unit_type_id !== -1) {
      filteredGroups = filteredGroups.filter(group => group.unitType.id === filterState.unit_type_id);
    }

    if (search !== "") {
      filteredGroups = filteredGroups
        ?.map(group => {
          const unitTypeMatches = group.unitType.title.toLowerCase().includes(search);
          const unitsMatchingTitle = group.units.filter(unit => unit.title.toLowerCase().includes(search));

          const matchingReservations = group.reservations.filter(res => {
            const customerName = (res.customer?.full_name || "").toLowerCase();
            const firstName = (res.customer?.first_name || "").toLowerCase();
            const lastName = (res.customer?.last_name || "").toLowerCase();
            return customerName.includes(search) || firstName.includes(search) || lastName.includes(search);
          });

          // If the unit type itself matches, show everything in that group
          if (unitTypeMatches) {
            return group;
          }

          // Otherwise, collect units that either match the title directly or have a matching reservation
          if (unitsMatchingTitle.length > 0 || matchingReservations.length > 0) {
            const unitIdsFromReservations = new Set();
            matchingReservations.forEach(res => {
              res.units?.forEach(u => unitIdsFromReservations.add(u.id));
            });

            const allMatchingUnits = group.units.filter(
              unit => unit.title.toLowerCase().includes(search) || unitIdsFromReservations.has(unit.id),
            );

            return {
              ...group,
              units: allMatchingUnits,
            };
          }

          return null;
        })
        .filter(Boolean);
    }

    setFilteredReservations(filteredGroups);
  }, [filterState.unit_type_id, filterState.search, groupedReservations]);

  function groupReservationsByUnitType(
    unitTypes: IAccommodationUnitType[],
    reservations: IAccommodationReservation[],
    allUnits: IAccommodationUnit[],
    unitTypePricesMap: Record<number, Record<string, number>>,
  ) {
    return unitTypes.map(unitType => {
      const unitsForType = allUnits.filter(unit => unit.unit_type_id === unitType.id);
      const reservationsForType = reservations.filter(reservation => reservation.unit_type_id === unitType.id);
      const pricesForType = unitTypePricesMap[unitType.id] || {};

      return {
        unitType,
        units: unitsForType,
        reservations: reservationsForType,
        pricesByDate: pricesForType,
      };
    });
  }

  async function loadAccommodationReservationData(token?: CancelToken) {
    const params = {
      start_date: formatDate(filterState?.startingDate),
      end_date: formatDate(filterState?.endingDate),
      with_units: true,
      with_reservation_units: true,
    };

    const accommodationReservationsRes = await GetAccommodationReservations(params, false);

    if (token && token.reason) {
      return;
    }

    if (accommodationReservationsRes.status !== StatusCode.OK) {
      dispatch(showError("Error loading reservations"));
      return;
    }

    const accommodationUnitTypesRes = await GetAccommodationUnitTypes(
      {
        start_date: formatDate(filterState?.startingDate),
        end_date: formatDate(filterState?.endingDate),
        with_availability: true,
      },
      false,
    );
    if (token && token.reason) {
      return;
    }
    if (accommodationUnitTypesRes.status !== StatusCode.OK) {
      setAccommodationUnitTypes([]);
      return;
    }

    const accommodationUnitsRes = await GetAccommodationUnits(null, false);
    if (token && token.reason) {
      return;
    }
    if (accommodationUnitsRes.status !== StatusCode.OK) {
      return;
    }

    const pricesRes = await GetAccommodationRatePlanPrices(
      { start_date: formatDate(filterState?.startingDate), end_date: formatDate(filterState?.endingDate) },
      false,
    );
    if (pricesRes.status !== StatusCode.OK) {
      dispatch(showError("Error loading rate plan prices"));
      return;
    }

    const reservations: IAccommodationReservation[] = accommodationReservationsRes?.data;
    const unitTypes: IAccommodationUnitType[] = accommodationUnitTypesRes?.data;
    const units: IAccommodationUnit[] = accommodationUnitsRes?.data;

    const unitTypePricesMap = buildUnitTypePricesMap(pricesRes.data as IAccommodationRatePlanPrice[]);
    const grouped = groupReservationsByUnitType(unitTypes, reservations, units, unitTypePricesMap);

    setGroupedReservations(grouped);
    setAccommodationUnitTypes(accommodationUnitTypesRes.data as IAccommodationUnitType[]);
  }

  function setDateRangeStart(date: Date) {
    setFilterState(prev => ({ ...prev, startingDate: date }));
  }

  function setDateRangeEnd(date: Date) {
    setFilterState(prev => ({ ...prev, endingDate: date }));
  }

  function buildUnitTypePricesMap(prices: IAccommodationRatePlanPrice[]) {
    const normalize = (d: string | Date) => moment(d).utc().format("YYYY-MM-DD");
    const map: Record<number, Record<string, number>> = {};

    prices.forEach(p => {
      const dateStr = normalize(p.date);
      const ut = p.unit_type_id;
      if (!map[ut]) {
        map[ut] = {};
      }
      const priceNum = Number(p.price);
      if (!Number.isFinite(priceNum)) {
        return;
      }
      if (map[ut][dateStr] === undefined || priceNum < map[ut][dateStr]) {
        map[ut][dateStr] = priceNum;
      }
    });
    return map;
  }

  return (
    <Page
      className="mt-4"
      // title={t("Accommodation Calendar")}
      // subtitle={t("View and manage your accommodation calendar")}
      // breadcrumbs={[
      //   { label: t("Accommodations"), url: "/admin/accommodation" },
      //   { label: t("Property Configuration"), url: "/admin/accommodation/configuration/locations" },
      //   { label: t("Calendar"), url: "/admin/accommodation/calendar" },
      // ]}
    >
      <Card
        titleActions={[
          {
            action: handleOpenNewReservation,
            content: (
              <>
                <FontAwesomeIcon size="lg" className="mr-2" icon={faPlus} /> {t("Create Reservation")}
              </>
            ),
            buttonType: "primary",
          },
        ]}
        title="Accommodation Calendar"
        subtitle="Manage your accommodation calendar here"
      >
        <hr style={{ marginBottom: "0px" }} />
        <Card.Section>
          <div className="flex justify-between items-center">
            <div className="w-sm">
              <Select
                onChange={(value: number) => setFilterState(prev => ({ ...prev, unit_type_id: Number(value) }))}
                defaultValue={-1}
                placeholder="Select unit type"
              >
                <Select.Option value={-1} name={"All"}>
                  {t("All")}
                </Select.Option>
                {accommodationUnitTypes?.map((type: IAccommodationUnitType) => (
                  <Select.Option key={type.id} value={type.id} name={type.title}>
                    {type.title}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div className="w-sm">
              <Search
                historyKey={"accommodation_calendar_search"}
                searchCallback={searchValue => {
                  setFilterState(prev => ({
                    ...prev,
                    search: searchValue,
                  }));
                }}
                leftIcon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
                placeholder={t("Search")}
              />
            </div>
          </div>
        </Card.Section>

        <Card.Section>
          <AccommodationReservationChart
            startDate={filterState?.startingDate}
            endDate={filterState?.endingDate}
            onEndDateChange={setDateRangeEnd}
            onStartDateChange={setDateRangeStart}
            data={filteredReservations}
            onClick={(unitId, date) => {
              setNewReservationState(prev => ({
                ...prev,
                arrival_date: date,
                departure_date: new Date(new Date(date).setDate(date.getDate() + 1)),
                openNewReservation: true,
              }));
            }}
          />
        </Card.Section>
      </Card>

      <AccommodationReservationModal
        newReservationState={newReservationState}
        setNewReservationState={setNewReservationState}
        billingState={billingState}
        setBillingState={setBillingState}
        rooms={rooms}
        setRooms={setRooms}
        selectedUnitTypes={selectedUnitTypes}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        steps={steps}
        accommodationCategories={accommodationCategories}
        mergedUnitTypes={mergedUnitTypes}
        customerPaymentMethods={customerPaymentMethods}
        onClose={handleCloseModal}
        handleContinue={handleContinue}
        handleBack={handleBack}
        handleBookReservation={handleBookReservation}
        handleCustomerSelection={handleCustomerSelection}
        handleCardInputChange={handleCardInputChange}
        handleCheckboxChange={handleCheckboxChange}
      />

      <ConfirmReservationModal
        isOpen={reservationCompleteState}
        onClose={handleCloseConfirmation}
        newReservationState={newReservationState}
        accommodationUnitTypes={accommodationUnitTypes}
        selectedUnitTypes={selectedUnitTypes}
      />
    </Page>
  );
}
